/**
 * StatsCard Component
 * 
 * Reusable card component for displaying statistical information
 * in the reporting dashboard with medical-grade styling
 */

import React from 'react';
import { Card } from 'react-bootstrap';
import PropTypes from 'prop-types';

const StatsCard = ({ 
    icon: Icon, 
    title, 
    value, 
    description, 
    iconColor = 'primary',
    trend = null,
    isLoading = false 
}) => {
    const getIconBgClass = (color) => {
        const colorMap = {
            primary: 'bg-primary bg-opacity-10',
            success: 'bg-success bg-opacity-10',
            info: 'bg-info bg-opacity-10',
            warning: 'bg-warning bg-opacity-10',
            danger: 'bg-danger bg-opacity-10'
        };
        return colorMap[color] || colorMap.primary;
    };

    const getIconTextClass = (color) => {
        const colorMap = {
            primary: 'text-primary',
            success: 'text-success',
            info: 'text-info',
            warning: 'text-warning',
            danger: 'text-danger'
        };
        return colorMap[color] || colorMap.primary;
    };

    return (
        <Card className="stats-card border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
                <div className={`stat-icon ${getIconBgClass(iconColor)} rounded-circle p-3 me-3`}>
                    <Icon className={`${getIconTextClass(iconColor)} h4 mb-0`} />
                </div>
                <div className="flex-grow-1">
                    {isLoading ? (
                        <>
                            <div className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                            </div>
                            <div className="placeholder-glow">
                                <span className="placeholder col-4"></span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="d-flex align-items-center">
                                <h3 className="mb-0 fw-bold me-2">{value}</h3>
                                {trend && (
                                    <small className={`badge ${trend.type === 'up' ? 'bg-success' : trend.type === 'down' ? 'bg-danger' : 'bg-secondary'}`}>
                                        {trend.value}
                                    </small>
                                )}
                            </div>
                            <p className="text-muted mb-0 small">{description}</p>
                            {title && <p className="text-muted mb-0 fw-medium small">{title}</p>}
                        </>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

StatsCard.propTypes = {
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string.isRequired,
    iconColor: PropTypes.oneOf(['primary', 'success', 'info', 'warning', 'danger']),
    trend: PropTypes.shape({
        type: PropTypes.oneOf(['up', 'down', 'neutral']),
        value: PropTypes.string
    }),
    isLoading: PropTypes.bool
};

export default StatsCard;